import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import { Box, Card, Chip, IconButton, Stack, Tooltip } from '@mui/material';
import React from 'react';
import {
  BiBuilding,
  BiCalendar,
  BiDollar,
  BiInfoCircle,
  BiLayout,
  BiNote,
  BiTime,
  BiUser,
} from 'react-icons/bi';
import { BsFillPersonFill } from 'react-icons/bs';
import {
  CandidateDTO,
  getPresignedURLForGet,
  InvoiceDTO,
  InvoiceStatus,
  invoiceStatusMap,
} from 'wisestep-api';
import { getFormatDate, WsColors, WsSelect, WsTypography } from 'wisestep-base';

import { getCurrencySymbol } from '../../../utils';
import { openUrlInNewTab } from '../../../utils/openUrlInNewTab';

import InvoiceDetailsCard, { InvoiceDetailsCardAttribute, properties } from './InvoiceDetailsCard';

export const InvoiceStatusChip = ({ invoiceStatus }: { invoiceStatus: string }) =>
  invoiceStatus ? (
    <Chip
      style={{ borderRadius: 4 }}
      size="small"
      {...(InvoiceStatus[invoiceStatus] === InvoiceStatus.OVERDUE
        ? { color: 'error' }
        : { sx: { backgroundColor: WsColors.TheBlues100, color: WsColors.TheBlues800 } })}
      label={invoiceStatusMap[InvoiceStatus[invoiceStatus]]}
    />
  ) : null;

const RelatedInvoices = ({
  invoices,
  candidateName,
  candidates,
}: {
  invoices?: InvoiceDTO[] | null;
  candidateName?: string;
  candidates: CandidateDTO[];
}) => (
  <>
    <Card elevation={0} sx={{ p: 1, width: '24rem', mt: 4, mb: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <BiUser size={32} color={WsColors.TheBlues500} />
        {candidates && candidates.length > 0 && (
          <WsSelect
            variant="standard"
            name="candidateId"
            label=""
            selectArray={candidates.map((c) => ({ value: c.id, label: c.name }))}
            placeholder="Candidate name"
            defaultValue={candidates[0].id}
          />
        )}
      </Stack>
    </Card>
    {!invoices ? (
      <></>
    ) : invoices.length === 0 ? (
      <Card elevation={0} sx={{ p: 2 }}>
        No other invoices available for the selected candidate.
      </Card>
    ) : (
      invoices.map((invoice, index) => (
        <Box key={index} pt={2}>
          <InvoiceDetailsCard
            icon={<BsFillPersonFill color={WsColors.TheBlues500} size={24} />}
            right={
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Tooltip title="View Invoice" placement="top">
                  <IconButton onClick={() => openUrlInNewTab(`/invoice/${invoice.id}`)}>
                    <RemoveRedEyeOutlinedIcon sx={{ color: WsColors.GreyBlack }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download Invoice" placement="top">
                  <IconButton
                    sx={{ m: -1 }}
                    onClick={async () => {
                      const {
                        data: { preSignedUrl },
                      } = await getPresignedURLForGet({
                        fileName: invoice.pdfFile.location,
                        downloadAs: invoice.invoiceCode ?? 'invoice',
                      });
                      window.open(preSignedUrl, '_blank');
                    }}
                  >
                    <FileDownloadOutlinedIcon sx={{ color: WsColors.GreyBlack }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            }
            title={candidateName ?? ''}
          >
            <InvoiceDetailsCardAttribute columns={4} tooltipTitle="Client Name">
              <BiBuilding />
              <WsTypography {...properties}>{invoice.client.name}</WsTypography>
            </InvoiceDetailsCardAttribute>
            <InvoiceDetailsCardAttribute columns={5} tooltipTitle="Invoice Date">
              <BiCalendar />
              <WsTypography {...properties}>
                {getFormatDate(String(invoice.raisedDate))}
              </WsTypography>
            </InvoiceDetailsCardAttribute>
            <InvoiceDetailsCardAttribute columns={3} tooltipTitle="Invoice Number">
              <BiLayout />
              <WsTypography {...properties}>{invoice.invoiceCode}</WsTypography>
            </InvoiceDetailsCardAttribute>
            <InvoiceDetailsCardAttribute columns={4} tooltipTitle="Invoice Type">
              <BiNote />
              <WsTypography {...properties}>{invoice.clientInvoiceType.name}</WsTypography>
            </InvoiceDetailsCardAttribute>
            <InvoiceDetailsCardAttribute columns={5} tooltipTitle="Invoice Receipt Status">
              <BiInfoCircle />
              <InvoiceStatusChip invoiceStatus={invoice.invoiceStatus} />
            </InvoiceDetailsCardAttribute>
            <InvoiceDetailsCardAttribute columns={3} tooltipTitle="Invoice Amount">
              <BiDollar />
              <WsTypography {...properties}>
                {invoice.currencyEntity && getCurrencySymbol(invoice.currencyEntity.symbol)}{' '}
                {isNaN(invoice.total) ? '' : invoice.total}
              </WsTypography>
            </InvoiceDetailsCardAttribute>
            <InvoiceDetailsCardAttribute columns={4} tooltipTitle="Due Date">
              <BiTime />
              <WsTypography {...properties}>
                {invoice.dueDate
                  ? `Due in ${Math.ceil(
                      (new Date(invoice.dueDate).getTime() - new Date().getTime()) /
                        (1000 * 60 * 60 * 24)
                    )} days`
                  : ''}
              </WsTypography>
            </InvoiceDetailsCardAttribute>
          </InvoiceDetailsCard>
        </Box>
      ))
    )}
  </>
);

export default RelatedInvoices;
